import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import type { Account, AccountType } from '../types';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

const ACCOUNT_TYPE_VARIANTS = {
  ASSET: 'default',
  LIABILITY: 'destructive',
  EQUITY: 'secondary',
  REVENUE: 'outline',
  EXPENSE: 'outline',
} as const;

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

export function Accounts() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<AccountType | ''>('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function fetchAccounts() {
      try {
        const data = await api.accounts.list();
        setAccounts(data);
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      } finally {
        setLoading(false);
      }
    }
    void fetchAccounts();
  }, []);

  const filteredAccounts = accounts.filter((account) => {
    const matchesType = !filter || account.type === filter;
    const matchesSearch = !search ||
      account.name.toLowerCase().includes(search.toLowerCase()) ||
      account.code.toLowerCase().includes(search.toLowerCase());
    return matchesType && matchesSearch;
  });

  const totalsByType = accounts.reduce((acc, account) => {
    if (!acc[account.type]) {
      acc[account.type] = 0;
    }
    acc[account.type] += account.balance;
    return acc;
  }, {} as Record<AccountType, number>);

  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Math.abs(amount));
  }, []);

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter(e.target.value as AccountType | '');
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
        <i className="bi bi-arrow-repeat text-2xl mb-3 animate-spin"></i>
        <p>Loading accounts...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Chart of Accounts</h1>
          <p className="text-sm text-muted-foreground">{accounts.length} accounts</p>
        </div>
        <Button variant="default">
          <i className="bi bi-plus mr-2"></i> Add Account
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-1">Total Assets</p>
            <p className="text-2xl font-bold font-mono" style={{ color: 'var(--color-primary)' }}>
              {formatCurrency(totalsByType.ASSET ?? 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-1">Total Liabilities</p>
            <p className="text-2xl font-bold font-mono text-red-600">
              {formatCurrency(totalsByType.LIABILITY ?? 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-1">Total Equity</p>
            <p className="text-2xl font-bold font-mono text-purple-600">
              {formatCurrency(totalsByType.EQUITY ?? 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-1">Net Income</p>
            <p className="text-2xl font-bold font-mono text-green-600">
              {formatCurrency((totalsByType.REVENUE ?? 0) - (totalsByType.EXPENSE ?? 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Account Type</label>
              <select
                className="px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm"
                value={filter}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="ASSET">Assets</option>
                <option value="LIABILITY">Liabilities</option>
                <option value="EQUITY">Equity</option>
                <option value="REVENUE">Revenue</option>
                <option value="EXPENSE">Expenses</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Search</label>
              <input
                type="text"
                className="px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm"
                placeholder="Search accounts..."
                value={search}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts Table */}
      <Card>
        <CardHeader>
          Accounts ({filteredAccounts.length})
        </CardHeader>
        <CardContent>
          {filteredAccounts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Code</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Name</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Type</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Subtype</th>
                    <th className="text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Balance</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Status</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((account) => (
                    <tr key={account.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-3">
                        <code className="text-sm">{account.code}</code>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/accounts/${account.id}`} className="text-foreground hover:text-primary">
                          {account.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={ACCOUNT_TYPE_VARIANTS[account.type] as BadgeVariant}>
                          {account.type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{account.subtype ?? '-'}</td>
                      <td className={`px-4 py-3 text-right font-mono ${account.balance < 0 ? 'text-red-600' : ''}`}>
                        {formatCurrency(account.balance)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={account.is_active ? 'secondary' : 'outline'}>
                          {account.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/accounts/${account.id}`}>
                          <Button variant="ghost" size="sm">View</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <i className="bi bi-folder2-open text-4xl text-muted-foreground mb-3"></i>
              <h3 className="text-lg font-semibold text-foreground mb-1">No accounts found</h3>
              <p className="text-muted-foreground mb-4">
                {search || filter ? 'Try adjusting your filters' : 'Get started by adding your chart of accounts'}
              </p>
              {!search && !filter && (
                <Button variant="default">
                  <i className="bi bi-plus mr-2"></i> Add Account
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <i className="bi bi-info-circle mr-2"></i> About Account Balances
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Account balances are calculated from the <code className="text-xs bg-muted px-1 py-0.5 rounded">account_balances</code> aggregate table.
            This table tracks running balances for each account, derived from{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">account_balance_edges</code> which link to source transactions.
          </p>
          <p className="text-muted-foreground text-sm mt-2">
            <strong>Balance Rules:</strong>
          </p>
          <ul className="text-muted-foreground text-sm ml-4 mt-1">
            <li><strong>Assets & Expenses:</strong> Debits increase balance</li>
            <li><strong>Liabilities, Equity & Revenue:</strong> Credits increase balance</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}